

import { MODULE } from "@/config/modules";
import { SaleType } from "@/models/base";
import { FriendlyNumberConfig } from "@/models/dossier-configs/friendly-number-config";
import { FriendlyNumberConfigs } from "@/models/dossier-configs/friendly-number-configs";
import { AuthService } from "@/services/auth-service";
import { BookYearService } from "@/services/book-year-service";
import { DossierConfigService } from "@/services/dossier-config-service";
import { SaleService } from "@/services/sale-service";
import { HttpError } from "@/utils/services/http-error";
import Vue from "vue";
import AppFriendlyIdEdit from "./FriendlyIdEdit.vue";

export default Vue.extend({
	data() {
		return {
			configs: null as null | FriendlyNumberConfigs,
			editModal: false,
			editedItem: null as FriendlyNumberConfig | null,
			editedKey: "Clients" as keyof FriendlyNumberConfigs,
			editedSaleTypeId: 0,
			saleTypes: SaleService.getAllTypes().filter(t=>t.ID != 2),
			saleOverlapErrorIds: [] as number[]
		};
	},
	props: {
		bookyear: {
			type: Number,
			default: () => {
				return BookYearService.selectedBookYear;
			}
		},
		showItemsWithPartition: {
			type: Boolean,
			default: false
		},
		resetFriendlyConfig: {
			type: Boolean,
			default: false
		},
	},
	computed: {
		editedFirstNumberExample(): string {
			if (!this.editedItem) return "";
			return this.getFirstNumberExample(this.editedItem);
		},
		selectedBookYear(): number {
			return BookYearService.selectedBookYear;
		},
		translatedKey(): string {
			if (this.editedSaleTypeId != 0){
				let t = this.saleTypes.find(s=>s.ID == this.editedSaleTypeId);
				if (!t) {
					return this.$ct("common.sale").toString();
				}
				return t.DisplayedName;
			}
			return this.translateKey(this.editedKey);
		},
		filteredConfig(): any {
			if (!this.configs) {
				return;
			}
			let c = this.configs.getNormalConfigKeys();
			if (this.showItemsWithPartition) {
				return c.filter((i, j) => {
					if (j > 2)
						return i;
				});
			}
			return c;
		},
		hasJournalModule():boolean {
			return AuthService.hasModule(MODULE.TOT_99_JOURNALEN);
		}
	},
	methods: {
		getFirstNumberExample(conf: FriendlyNumberConfig): string {
			if (!conf) return "";
			let result = conf.Prefix;
			let selectedBookyear = this.bookyear;
			let bookyearShort = `${this.bookyear}`.substring(2);
			result = result.replace("{bookyearShort}", bookyearShort);
			result = result.replace("{bookyear}", selectedBookyear + "");
			let number = conf.StartingNumber + "";
			for (let i = conf.NumberLength; i > number.length; i--) {
				result += "0";
			}
			return result + number;
		},
		edit(key: keyof FriendlyNumberConfigs) {
			if (!this.configs) return;
			this.editedSaleTypeId = 0;
			this.editedKey = key;
			this.editedItem = new FriendlyNumberConfig((this.configs[key] as FriendlyNumberConfig).getJSON());
			this.editModal = true;
		},
		editSaleType(saleType:SaleType){
			if (!this.configs) return;
			this.editedSaleTypeId = saleType.ID;
			this.editedItem = new FriendlyNumberConfig((this.configs.SaleConfigs[`${saleType.ID}`]));
			this.editModal = true;
		},
		cancel() {
			this.editedItem = null;
			this.editModal = false;
		},
		async save() {
			if (!this.editedItem || !this.configs) return;
			if (this.editedItem.EndNumber != 0) {
				if (this.editedItem.EndNumber < this.editedItem.StartingNumber) {
					this.$wf.notify(this.$ct("end-number-has-to-be-0-or-greater-then-starting-number"), "danger", 3000, "exclamation-triangle");
					return;
				}
			}
			if (this.editedItem.Prefix.charAt(0) == "-") {
				this.$wf.notify(this.$ct("error-cannot-use-dash"), "danger", 3000, "exclamation-triangle");
				return;
			}
			this.resetOverlapErrors();
			if (this.editedSaleTypeId != 0) {
				this.configs.SaleConfigs[`${this.editedSaleTypeId}`] = this.editedItem;
			}else{
				this.configs.setConfigKeyValue(this.editedKey, this.editedItem);
			}
			try{
				await DossierConfigService.saveFriendlyNumberConfigs(this.configs);
				this.editedItem = null;
				this.editedSaleTypeId = 0;
				this.editModal = false;
			}catch(err){
				let hErr = err as HttpError;
				if (hErr.key == "DOSSIER_CONFIG_FRIENDLY_NUMBER_OVERLAP"){
					this.onOverlap(hErr.data);
				}
			}
		},
		resetOverlapErrors(){
			this.saleOverlapErrorIds = [];
		},
		onOverlap(data:string[]){
			for (let item of data) {
				if (/^S[0-9]+$/.test(item)){
					this.saleOverlapErrorIds.push(parseInt(item.substring(1)));
				}
			}
		},
		translateKey(key: string): string {
			let result:string | undefined = undefined;
			if (result) {
				return result;
			}
			return this.$t("common.browse." + key).toString();
		},
		translateSaleTypeKey(saleType:SaleType):string {
			return saleType.DisplayedName;
		},
		getStartingNumber(n: number) {
			let factor = (n.toString().length);
			let d = n.toString()[0];
			d = d.padEnd(factor, "0");
			if (factor <= 4) {
				return 1;
			}
			return parseInt(d) + 1;
		},
		getKeyDisabledMessage(key:keyof FriendlyNumberConfigs):string {
			if (!this.hasJournalModule){
				return "";
			}
			if (key != "Purchase") {
				return "";
			}
			return `(${this.$ct("edit-numbering-in-journals")})`;
		},
		getSaleKeyDisabledMessage(saleType:SaleType):string{
			if (!this.hasJournalModule){
				return "";
			}
			if (saleType.ID != 1) {
				return "";
			}
			return `(${this.$ct("edit-numbering-in-journals")})`;
		},
		hasSaleTypeError(t:SaleType):boolean{
			for (let typeId of this.saleOverlapErrorIds){
				if (t.ID == typeId){
					return true;
				}
			}
			return false;
		}
	},
	async mounted() {
		this.configs = await DossierConfigService.getFriendlyNumbers();
		if (this.resetFriendlyConfig) {
			this.filteredConfig.map((i: keyof FriendlyNumberConfigs) => {
				if (this.configs == null) {
					return i;
				}
				let c = new FriendlyNumberConfig((this.configs[i as keyof FriendlyNumberConfigs] as FriendlyNumberConfig).getJSON());
				if (c.StartingNumber != 1) {
					c.StartingNumber = this.getStartingNumber(c.StartingNumber);
					this.editedItem = c;
					this.editedKey = i;
					this.save();
				}
			});
		}
	},
	components: {
		AppFriendlyIdEdit
	},
	i18nEx: {
		componentPrefix: "components.settings-default"
	}
});
